<template>
  <div class="relative inline-block text-right" ref="wrap">
    <button
        type="button"
        class="w-full px-2 py-1.5 bg-gray-800/60 text-xs rounded-lg  flex items-center justify-between transition-all duration-200 "
        @click="toggle"
        :class="open ? 'z-[9999] ring-1 ring-blue-500/30 border-cyan-500/40' : ''"
    >
      <span class="truncate text-gray-200">
        {{ displayLabel || placeholder }}
      </span>
      <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-gray-400 transition-transform duration-200"
           :class="open ? 'rotate-180' : ''" viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.08 1.04l-4.25 4.25a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clip-rule="evenodd"/>
      </svg>
    </button>

    <div
        v-if="open"
        class="absolute z-50 mt-1 w-[22rem] max-w-[70vw] bg-gray-900/95 border border-white/15 rounded-xl shadow-2xl p-3 backdrop-blur-xl backdrop-saturate-150"
        @keydown.stop
        style="box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 20px rgba(168, 85, 247, 0.15);"
    >
      <!-- Search input -->
      <input
          style="z-index: 999999"
          ref="search"
          v-model.trim="q"
          @keydown.down.prevent="move(1)"
          @keydown.up.prevent="move(-1)"
          @keydown.enter.prevent="chooseHover()"
          @keydown.esc.prevent="close()"
          type="text"
          class="w-full px-3 py-2 text-xs bg-gray-800/70 border border-white/10 rounded-lg text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-transparent transition-all duration-200"
          placeholder="جستجو …"
      />

      <!-- Options list -->
      <div class="max-h-56 overflow-auto mt-3 space-y-1 custom-scrollbar">
        <!-- Clear option -->
        <div
            v-if="allowClear"
            class="px-3 py-2 text-[11px] text-gray-300 hover:bg-gray-800/80 hover:text-white cursor-pointer rounded-lg transition-colors duration-150 flex items-center justify-between group"
            @click="select(null)"
        >
          <span class="group-hover:text-emerald-300 transition-colors">همه </span>
          <span class="text-[10px] opacity-0 group-hover:opacity-70 transition-opacity">↶</span>
        </div>

        <!-- Options -->
        <div
            v-for="(b, i) in filtered"
            style="z-index: 999999"
            :key="b.id"
            class=" px-3 py-2 text-xs rounded-lg cursor-pointer flex items-center justify-between transition-all duration-150"
            :class="[
              i === hover
                ? 'bg-gradient-to-r from-purple-900/40 to-pink-900/30 border border-purple-500/30 text-white shadow-md'
                : 'hover:bg-gray-800/70 text-gray-300 hover:text-white border border-transparent hover:border-white/5'
            ]"
            @mouseenter="hover = i"
            @mousedown.prevent="select(b.id)"
        >
          <span class="truncate font-medium">{{ b.name }}</span>
          <span v-if="value === b.id" class="text-[10px] text-emerald-400 animate-pulse">✓</span>
          <span v-else-if="i === hover" class="text-[10px] text-purple-300 opacity-70">→</span>
        </div>

        <!-- No results -->
        <div v-if="!filtered.length" class="px-3 py-4 text-center text-[11px] text-gray-400">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mx-auto mb-1 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          چیزی پیدا نشد
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "SearchableSelect",
  props: {
    options: { type: Array, required: true },
    value: { required: false, default: "" },
    placeholder: { type: String, default: "انتخاب کنید" },
    allowClear: { type: Boolean, default: true },
  },
  data() {
    return { open: false, q: "", hover: 0 };
  },
  computed: {
    filtered() {
      const q = this.q.toLowerCase();
      return !q ? this.options : this.options.filter(b => (b.name || "").toLowerCase().includes(q));
    },
    displayLabel() {
      const found = this.options.find(b => b.id === this.value);
      return found?.name || "";
    }
  },
  methods: {
    toggle() {
      this.open ? this.close() : this.openAndFocus();
    },
    openAndFocus() {
      this.open = true;
      this.$nextTick(() => {
        // this.$refs.search.focus();
        this.$refs.search.select();
      });
      this.hover = 0;
    },
    close() {
      this.open = false;
      this.q = "";
      this.hover = 0;
    },
    move(step) {
      const len = this.filtered.length;
      if (!len) return;
      this.hover = ((this.hover + step) % len + len) % len;
    },
    chooseHover() {
      const b = this.filtered[this.hover];
      if (b) this.select(b.id);
    },
    select(id) {
      this.$emit("input", id);
      this.$emit("change", id);
      this.close();
    },
    onClickOutside(e) {
      if (!this.open) return;
      const el = this.$refs.wrap;
      if (el && !el.contains(e.target)) this.close();
    }
  },
  mounted() {
    document.addEventListener("mousedown", this.onClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener("mousedown", this.onClickOutside);
  }
};
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(168, 85, 247, 0.4);
  border-radius: 10px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(168, 85, 247, 0.6);
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.absolute.z-50 {
  animation: slideDown 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.bg-gradient-to-r {
  background-image: linear-gradient(90deg,
  rgba(168, 85, 247, 0.2),
  rgba(236, 72, 153, 0.15)
  );
}
</style>